import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../../utils/API";
import Swal from "sweetalert2";
import ServiceCategoryBadges from "../../components/serviceCategoryBadges";

function BossApprovalReview() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();

  const [serviceData, setServiceData] = useState(null);
  const [checklistData, setChecklistData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
    if (!u || !["BOSS", "ADMIN"].includes(u.role)) {
      navigate("/dashboard");
      return;
    }
    if (!id) {
      navigate("/boss/approvals");
      return;
    }

    const cancelToken = API.cancelToken();

    Promise.all([
      API.getOrder(cancelToken, id),
      API.getChecklistDetail(cancelToken, id),
    ])
      .then(([serviceRes, checklistRes]) => {
        setServiceData(serviceRes.data);
        setChecklistData(checklistRes.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.message === "cancelling") return;
        setLoading(false);
        Swal.fire("Erro", "Erro ao carregar dados do serviço.", "error");
      });

    return () => API.cancel(cancelToken);
  }, [id, navigate]);

  const handleApprove = () => {
    Swal.fire({
      title: "Liberar para compras?",
      text: "A equipe de compras poderá iniciar o orçamento e a aquisição das peças.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#389E0D",
      confirmButtonText: "Liberar para Compras",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      Swal.fire({ title: "Liberando...", showConfirmButton: false, allowOutsideClick: false, willOpen: () => Swal.showLoading() });

      API.postBossApproval(API.cancelToken(), id, "approve")
        .then(() => {
          Swal.fire({ title: "Liberado!", text: "Serviço enviado para a equipe de compras.", icon: "success", confirmButtonColor: "#389E0D" })
            .then(() => navigate("/boss/approvals"));
        })
        .catch(() => {
          Swal.fire("Erro", "Não foi possível liberar para compras.", "error");
        });
    });
  };

  const handleReject = () => {
    Swal.fire({
      title: "Rejeitar serviço?",
      text: "Informe o motivo da rejeição para o mecânico.",
      input: "textarea",
      inputPlaceholder: "Motivo da rejeição...",
      inputAttributes: { "aria-label": "Motivo da rejeição" },
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Informe o motivo da rejeição.";
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      Swal.fire({ title: "Rejeitando...", showConfirmButton: false, allowOutsideClick: false, willOpen: () => Swal.showLoading() });

      API.postBossApproval(API.cancelToken(), id, "reject", result.value)
        .then(() => {
          Swal.fire({ title: "Rejeitado", text: "O mecânico será notificado para refazer o checklist.", icon: "info", confirmButtonColor: "#389E0D" })
            .then(() => navigate("/boss/approvals"));
        })
        .catch(() => {
          Swal.fire("Erro", "Erro ao rejeitar serviço.", "error");
        });
    });
  };

  if (loading) return null;

  const vehicle = serviceData?.vehicle;
  const checklist = checklistData?.checklist || {};
  const serviceItems = checklistData?.items || [];
  const images = serviceData?.images || [];
  const audios = serviceData?.audios || [];

  // Group checklist items by position (items may contain " - " separator for position)
  const statusColors = {
    Bom: "bg-green-100 text-green-800",
    Regular: "bg-yellow-100 text-yellow-800",
    Ruim: "bg-red-100 text-red-800",
  };

  return (
    <div className="flex flex-col self-stretch px-20 pt-8 pb-12 max-md:px-4">
      {/* Header */}
      <div className="flex items-center justify-between py-4 px-6 bg-white rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <Link to="/boss/approvals" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">Revisão OS #{id}</h1>
        </div>
      </div>

      {/* Vehicle Info — client data intentionally omitted for the workshop chief */}
      <div className="mt-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Veículo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div className="flex justify-between sm:block">
              <span className="text-gray-500 sm:block">Placa</span>
              <span className="font-medium text-gray-900">{vehicle?.license_plate || "-"}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-gray-500 sm:block">Modelo</span>
              <span className="font-medium text-gray-900">{vehicle?.model || "-"}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-gray-500 sm:block">Ano</span>
              <span className="font-medium text-gray-900">{vehicle?.year || "-"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Service categories chosen at intake */}
      {(() => {
        const codes = serviceData?.service_categories;
        const hasCodes = Array.isArray(codes) ? codes.length > 0 : !!codes;
        if (!hasCodes) return null;
        return (
          <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Tipo de Serviço</h2>
            <ServiceCategoryBadges codes={codes} />
          </div>
        );
      })()}

      {/* Problem Description */}
      {serviceData?.problem_description && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Descrição do Problema</h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{serviceData.problem_description}</p>
        </div>
      )}

      {/* Images & Videos */}
      {images.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Mídias</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {images.map((img, idx) => {
              const url = img.image_url;
              const ext = String(url).split("?")[0].split(".").pop().toLowerCase();
              const isVideo = ["mp4", "webm", "mov", "m4v", "mkv"].includes(ext);
              return isVideo ? (
                <video
                  key={idx}
                  src={url}
                  controls
                  className="w-full h-32 object-cover rounded-lg border border-gray-200 bg-black"
                />
              ) : (
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Imagem ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Audios */}
      {audios.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Áudios</h2>
          <div className="space-y-2">
            {audios.map((audio, idx) => (
              <audio key={idx} controls className="w-full">
                <source src={audio.audio_url} />
              </audio>
            ))}
          </div>
        </div>
      )}

      {/* Checklist Items */}
      {Object.keys(checklist).length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Checklist Obrigatório</h2>
          <div className="space-y-2">
            {Object.entries(checklist).map(([item, details]) => (
              <div key={item} className="flex items-start justify-between py-2.5 px-3 rounded-lg bg-gray-50 gap-4">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900">{item}</span>
                  {details.observation && (
                    <p className="text-xs text-gray-500 mt-0.5">{details.observation}</p>
                  )}
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[details.status] || "bg-gray-100 text-gray-600"}`}>
                  {details.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Items (additional services added by mechanic) */}
      {serviceItems.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Serviços Adicionados pelo Mecânico</h2>
          <div className="space-y-2">
            {serviceItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-gray-50">
                <span className="text-sm font-medium text-gray-900">{item.service}</span>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {item.hht && <span>{item.hht}h</span>}
                  {item.annotation && <span className="max-w-[200px] truncate">{item.annotation}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          onClick={handleReject}
          className="px-6 py-2.5 rounded-lg border-2 border-red-500 text-red-600 font-medium hover:bg-red-50 transition-colors"
        >
          Rejeitar
        </button>
        <button
          onClick={handleApprove}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#389E0D] text-white font-medium hover:bg-green-700 transition-colors"
        >
          Liberar para Compras
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default BossApprovalReview;
